import React, { useState } from 'react';
import { Shield, Building2, User, DollarSign, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { useSocialSecurityConfig } from '../../../hooks/useSocialSecurityConfig';
import { useVariableIncomes } from '../../../hooks/useVariableIncomes';
import useEmployees from '../../../hooks/useEmployees';
import { getHealthDependentsSummary } from '../../../api/relatedPersons';
import { payrollConfigAPI } from '../../../api/payrollConfig';
import { prestacionesAPI } from '../../../api/prestaciones';
import { salaryHistoryAPI } from '../../../api/salaryHistory';
import { useCompany } from '../../../contexts/CompanyContext';

const fmt = (n, dec = 2) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: dec });

const pct = (v, fallback = 0) =>
  `${(parseFloat(v || fallback) * 100).toFixed(2)}%`;

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, valueClass = 'text-gray-900' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-gray-400" />
        <p className="text-xs font-medium text-gray-500">{label}</p>
      </div>
      <p className={`text-base font-bold ${valueClass}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function SocialSecurityModule() {
  const { employees } = useEmployees();
  const { getVariableIncomes } = useVariableIncomes();
  const { globalConfig, configs, loading, getEffectiveConfig, calculateSS } =
    useSocialSecurityConfig();
  const { currentCompany } = useCompany();

  const [selectedPeriod, setSelectedPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [activeTab, setActiveTab]           = useState('empleado');
  const [capitaData, setCapitaData]             = useState({});
  const [historicalSalaries, setHistoricalSalaries] = useState({});
  const [salariosMensuales, setSalariosMensuales]   = useState({});

  // Cargar cápita = dependent_count (Personas Relacionadas) × capita_monto (PayrollConfig)
  React.useEffect(() => {
    Promise.all([
      payrollConfigAPI.getParametros(),
      getHealthDependentsSummary(),
    ])
      .then(([params, rows]) => {
        const capParam  = Array.isArray(params) ? params.find(p => p.parametro === 'capita_monto') : null;
        const montoUnit = capParam ? parseFloat(capParam.valor) || 0 : 0;
        const map       = {};
        rows.forEach(r => {
          map[r.employee_id] = parseInt(r.dependent_count || 0) * montoUnit;
        });
        setCapitaData(map);
      })
      .catch(() => setCapitaData({}));
  }, []);
  // Cargar ingresos variables de nóminas cerradas del período (comisiones, incentivos)
  React.useEffect(() => {
    if (!currentCompany?.id) { setHistoricalSalaries({}); return; }
    prestacionesAPI.getSalariosPeriodo(currentCompany.id, selectedPeriod)
      .then(map => setHistoricalSalaries(map || {}))
      .catch(() => setHistoricalSalaries({}));
  }, [selectedPeriod, currentCompany?.id]);

  // Salario mensual vigente al último día del período desde salary_history/contrato
  React.useEffect(() => {
    if (!currentCompany?.id || !selectedPeriod) { setSalariosMensuales({}); return; }
    const [yyyy, mm] = selectedPeriod.split('-');
    const lastDay = new Date(parseInt(yyyy), parseInt(mm), 0).getDate();
    const fechaFin = `${selectedPeriod}-${String(lastDay).padStart(2, '0')}`;
    salaryHistoryAPI.getBulkAtDate(currentCompany.id, fechaFin)
      .then(map => setSalariosMensuales(map || {}))
      .catch(() => setSalariosMensuales({}));
  }, [selectedPeriod, currentCompany?.id]);

  const [showRates, setShowRates]               = useState(false);
  const [variableIncomesData, setVariableIncomesData] = useState({});

  // Cargar Q1 + Q2 (ingresos variables)
  React.useEffect(() => {
    const loadAllIncomes = async () => {
      if (!employees.length) return;
      const incomesMap = {};
      for (const emp of employees.filter(e => e.estado === 'activo')) {
        incomesMap[emp.id] = { comisiones: 0, incentivos: 0 };
        try {
          const [dataQ1, dataQ2] = await Promise.all([
            getVariableIncomes(emp.id, selectedPeriod, 'Q1').catch(() => null),
            getVariableIncomes(emp.id, selectedPeriod, 'Q2').catch(() => null),
          ]);
          incomesMap[emp.id] = {
            comisiones: parseFloat(dataQ1?.comisiones || 0) + parseFloat(dataQ2?.comisiones || 0),
            incentivos:  parseFloat(dataQ1?.incentivos  || 0) + parseFloat(dataQ2?.incentivos  || 0),
          };
        } catch (error) {
          console.warn(`Error loading incomes for ${emp.nombre}:`, error);
        }
      }
      setVariableIncomesData(incomesMap);
    };
    loadAllIncomes();
  }, [employees, selectedPeriod, getVariableIncomes, configs]);

  // Calcular SS por empleado
  const employeesWithCalcs = employees
    .filter(e => e.estado === 'activo')
    .map(emp => {
      const effectiveConfig = getEffectiveConfig(emp.id);

      // Salario base mensual desde salary_history al último día del período.
      // Fallback: salario del contrato activo (emp.salario).
      const salarioBase = salariosMensuales[emp.id] ?? parseFloat(emp.salario || 0);

      // Variables: preferir datos reales de nóminas cerradas (historicalSalaries),
      // si no hay nóminas cerradas usar payroll_incomes (variableIncomesData).
      const historicalEntry = historicalSalaries[emp.id];
      const incomes = historicalEntry && typeof historicalEntry === 'object'
        ? { comisiones: historicalEntry.comisiones || 0, incentivos: historicalEntry.incentivos || 0 }
        : variableIncomesData[emp.id] || { comisiones: 0, incentivos: 0 };

      let ss = {
        salarioCotizable: salarioBase,
        afpEmpleado: 0, afpEmpleadoBruto: 0,
        sfsEmpleado: 0, sfsEmpleadoBruto: 0,
        totalEmpleado: 0, totalAbsorbido: 0,
        afpEmpleador: 0, sfsEmpleador: 0,
        srlEmpleador: 0, infotep: 0,
        totalEmpleador: 0, deductAFP: true, deductSFS: true,
      };

      if (globalConfig && effectiveConfig && calculateSS) {
        try {
          const result = calculateSS({
            salarioBase,
            comisiones:  incomes.comisiones,
            incentivos:  incomes.incentivos,
            employeeId:  emp.id,
          });
          if (result && result.totalEmpleador !== undefined) ss = result;
        } catch (error) {
          console.error(`Error calculating SS for ${emp.nombre}:`, error);
        }
      }

      return {
        ...emp,
        salarioBase,
        comisiones:    incomes.comisiones,
        incentivos:    incomes.incentivos,
        capitaMensual: capitaData[emp.id] || 0,
        config: effectiveConfig,
        ss,
      };
    });

  const totales = employeesWithCalcs.reduce((acc, emp) => {
    const capita = emp.capitaMensual || 0;
    acc.totalAFPEmpleado  += (emp.ss?.afpEmpleadoBruto  ?? emp.ss?.afpEmpleado ?? 0);
    acc.totalSFSEmpleado  += (emp.ss?.sfsEmpleadoBruto  ?? emp.ss?.sfsEmpleado ?? 0);
    acc.totalCapita       += capita;
    acc.totalADescontar   += (emp.ss?.totalEmpleado || 0) + capita;
    acc.totalAFPEmpleador += (emp.ss?.afpEmpleador || 0);
    acc.totalSFSEmpleador += (emp.ss?.sfsEmpleador || 0);
    acc.totalSRLEmpleador += (emp.ss?.srlEmpleador || 0);
    acc.totalINFOTEP      += (emp.ss?.infotep      || 0);
    acc.totalEmpleador    += (emp.ss?.totalEmpleador || 0) + (emp.ss?.totalAbsorbido || 0);
    acc.totalTSS          +=
      (emp.ss?.afpEmpleadoBruto ?? emp.ss?.afpEmpleado ?? 0) +
      (emp.ss?.sfsEmpleadoBruto ?? emp.ss?.sfsEmpleado ?? 0) +
      capita + (emp.ss?.totalEmpleador || 0);
    return acc;
  }, {
    totalAFPEmpleado: 0, totalSFSEmpleado: 0,
    totalCapita: 0, totalADescontar: 0,
    totalAFPEmpleador: 0, totalSFSEmpleador: 0,
    totalSRLEmpleador: 0, totalINFOTEP: 0,
    totalEmpleador: 0, totalTSS: 0,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  const n = employeesWithCalcs.length;

  return (
    <div className="space-y-4">

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          {n} empleado{n !== 1 ? 's' : ''} activo{n !== 1 ? 's' : ''}
        </span>

        <div className="flex items-center gap-1.5">
          <Calendar size={14} className="text-gray-400" />
          <input
            type="month"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
        </div>

        <button
          onClick={() => setShowRates(!showRates)}
          className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition-colors ${
            showRates
              ? 'border-blue-400 text-blue-600 bg-blue-50'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <Shield size={13} />
          Tasas vigentes
          {showRates ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {/* ── Rates panel (collapsible) ────────────────────────────── */}
      {showRates && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 grid grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Empleado (5.91%)
            </p>
            <div className="space-y-1 text-sm text-gray-700">
              <div className="flex justify-between">
                <span>AFP</span>
                <span className="font-semibold">{pct(globalConfig?.porcentaje_afp_empleado, 0.0287)}</span>
              </div>
              <div className="flex justify-between">
                <span>SFS</span>
                <span className="font-semibold">{pct(globalConfig?.porcentaje_sfs_empleado, 0.0304)}</span>
              </div>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Empleador (16.19%)
            </p>
            <div className="space-y-1 text-sm text-gray-700">
              <div className="flex justify-between">
                <span>AFP</span>
                <span className="font-semibold">{pct(globalConfig?.porcentaje_afp_empleador, 0.0710)}</span>
              </div>
              <div className="flex justify-between">
                <span>SFS</span>
                <span className="font-semibold">{pct(globalConfig?.porcentaje_sfs_empleador, 0.0709)}</span>
              </div>
              <div className="flex justify-between">
                <span>SRL</span>
                <span className="font-semibold">{pct(globalConfig?.porcentaje_srl_empleador, 0.0120)}</span>
              </div>
              <div className="flex justify-between">
                <span>INFOTEP</span>
                <span className="font-semibold">{pct(globalConfig?.porcentaje_infotep, 0.0100)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Stat cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={User}
          label="Empleados activos"
          value={n}
          valueClass="text-blue-600 text-xl"
        />
        <StatCard
          icon={Shield}
          label="TSS Total"
          value={`RD$ ${fmt(totales.totalTSS)}`}
          sub="Empleado + Empleador"
          valueClass="text-gray-900"
        />
        <StatCard
          icon={DollarSign}
          label="Total a Descontar"
          value={`RD$ ${fmt(totales.totalADescontar)}`}
          sub="Cargo al empleado"
          valueClass="text-red-600"
        />
        <StatCard
          icon={Building2}
          label="Costo Empleador"
          value={`RD$ ${fmt(totales.totalEmpleador)}`}
          sub="Incluye asumidos"
          valueClass="text-gray-900"
        />
      </div>

      {/* ── Tabs ────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 px-1 pt-1">
          <button
            onClick={() => setActiveTab('empleado')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'empleado'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <User size={14} />
            Aportes del Empleado
          </button>
          <button
            onClick={() => setActiveTab('empleador')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'empleador'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 size={14} />
            Aportes del Empleador
          </button>
        </div>

        {/* ── Tab: Empleado ─────────────────────────────────────── */}
        {activeTab === 'empleado' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="px-4 py-3 text-left   text-xs font-semibold uppercase tracking-wider">Empleado</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Salario Base</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Comisiones</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Incentivos</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Cotizable</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">AFP</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">SFS</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Cápita</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">TSS Total</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">A Descontar</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employeesWithCalcs.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center text-sm text-gray-400">
                      No hay empleados activos
                    </td>
                  </tr>
                ) : (
                  employeesWithCalcs.map((emp) => {
                    const { config, ss } = emp;
                    const capita         = emp.capitaMensual || 0;
                    const tssTotal       = (ss?.afpEmpleadoBruto ?? ss?.afpEmpleado ?? 0) +
                                          (ss?.sfsEmpleadoBruto ?? ss?.sfsEmpleado ?? 0) + capita;
                    const aDescontar     = (ss?.totalEmpleado ?? 0) + capita;
                    const deductAFP       = ss?.deductAFP ?? true;
                    const deductSFS       = ss?.deductSFS ?? true;
                    const hayAbsorbido    = (ss?.totalAbsorbido ?? 0) > 0;

                    return (
                      <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-900">{emp.nombre} {emp.apellido}</p>
                          <p className="text-xs text-gray-400">{emp.employeeId}</p>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                          RD$ {fmt(emp.salarioBase)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          {emp.comisiones > 0
                            ? <span className="font-medium text-gray-700">RD$ {fmt(emp.comisiones)}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          {emp.incentivos > 0 ? (
                            <div>
                              <span className="font-medium text-gray-700">RD$ {fmt(emp.incentivos)}</span>
                              {!config?.incluirIncentivos && (
                                <p className="text-xs text-orange-500">No incluido</p>
                              )}
                            </div>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <span className="font-semibold text-blue-700">
                            RD$ {fmt(ss?.salarioCotizable ?? 0)}
                          </span>
                          {(emp.comisiones > 0 || (emp.incentivos > 0 && config?.incluirIncentivos)) && (
                            <p className="text-xs text-gray-400">
                              Base{emp.comisiones > 0 && '+Com'}{emp.incentivos > 0 && config?.incluirIncentivos && '+Inc'}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <span className={deductAFP ? 'text-gray-700' : 'text-green-600 font-medium'}>
                            RD$ {fmt(ss?.afpEmpleadoBruto ?? ss?.afpEmpleado ?? 0)}
                          </span>
                          {!deductAFP && <p className="text-xs text-green-500">Asumido</p>}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <span className={deductSFS ? 'text-gray-700' : 'text-green-600 font-medium'}>
                            RD$ {fmt(ss?.sfsEmpleadoBruto ?? ss?.sfsEmpleado ?? 0)}
                          </span>
                          {!deductSFS && <p className="text-xs text-green-500">Asumido</p>}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          {capita > 0
                            ? <span className="font-medium text-amber-700">RD$ {fmt(capita)}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-bold text-blue-700">
                            RD$ {fmt(tssTotal)}
                          </span>
                          <p className="text-xs text-gray-400">
                            AFP+SFS{capita > 0 ? '+Cápita' : ''}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`text-sm font-bold ${aDescontar > 0 ? 'text-gray-900' : 'text-green-600'}`}>
                            {aDescontar > 0 ? '-' : ''}RD$ {fmt(aDescontar)}
                          </span>
                          {(!deductAFP || !deductSFS) && (
                            <p className="text-xs text-green-500">
                              {!deductAFP && !deductSFS ? 'AFP+SFS asumidos' : !deductAFP ? 'AFP asumido' : 'SFS asumido'}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {hayAbsorbido ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              Beneficio
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Descuento
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {employeesWithCalcs.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 border-t-2 border-gray-200">
                    <td colSpan={5} className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Totales
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                      RD$ {fmt(totales.totalAFPEmpleado)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                      RD$ {fmt(totales.totalSFSEmpleado)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-amber-700">
                      {totales.totalCapita > 0 ? `RD$ ${fmt(totales.totalCapita)}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-blue-700">
                      RD$ {fmt(totales.totalAFPEmpleado + totales.totalSFSEmpleado + totales.totalCapita)}
                    </td>
                    <td className="px-4 py-3 text-right text-base font-bold text-gray-900">
                      -RD$ {fmt(totales.totalADescontar)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* ── Tab: Empleador ────────────────────────────────────── */}
        {activeTab === 'empleador' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="px-4 py-3 text-left  text-xs font-semibold uppercase tracking-wider">Empleado</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Cotizable</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">AFP</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">SFS</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">SRL</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">INFOTEP</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Total Empleador</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employeesWithCalcs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-400">
                      No hay empleados activos
                    </td>
                  </tr>
                ) : (
                  employeesWithCalcs.map((emp) => {
                    const { ss } = emp;
                    return (
                      <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-900">{emp.nombre} {emp.apellido}</p>
                          <p className="text-xs text-gray-400">{emp.employeeId}</p>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-blue-700">
                          RD$ {fmt(ss?.salarioCotizable ?? 0)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700 font-medium">
                          RD$ {fmt(ss?.afpEmpleador ?? 0)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700 font-medium">
                          RD$ {fmt(ss?.sfsEmpleador ?? 0)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700 font-medium">
                          RD$ {fmt(ss?.srlEmpleador ?? 0)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700 font-medium">
                          RD$ {fmt(ss?.infotep ?? 0)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-bold text-gray-900">
                            RD$ {fmt(ss?.totalEmpleador ?? 0)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {employeesWithCalcs.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 border-t-2 border-gray-200">
                    <td colSpan={2} className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Totales
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                      RD$ {fmt(totales.totalAFPEmpleador)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                      RD$ {fmt(totales.totalSFSEmpleador)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                      RD$ {fmt(totales.totalSRLEmpleador)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                      RD$ {fmt(totales.totalINFOTEP)}
                    </td>
                    <td className="px-4 py-3 text-right text-base font-bold text-gray-900">
                      RD$ {fmt(totales.totalEmpleador)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

      </div>
    </div>
  );
}
